import { Calendar, TrendingUp, Tag } from 'lucide-react';
import { usePromos } from '../hooks/useData';

const promoTypeColors = {
  'Site-wide': 'bg-blue-100 text-blue-800',
  'Category': 'bg-green-100 text-green-800',
  'Marketplace': 'bg-purple-100 text-purple-800',
  'Festive': 'bg-orange-100 text-orange-800',
};

function PromoCard({ promo }) {
  const startDate = new Date(promo.start_date);
  const endDate = new Date(promo.end_date);
  const today = new Date();

  const isActive = today >= startDate && today <= endDate;
  const isUpcoming = today < startDate;

  const daysUntil = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
  const duration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 border-l-4 ${
      isActive ? 'border-l-green-500 bg-green-50' :
      isUpcoming ? 'border-l-blue-500' :
      'border-l-gray-300 opacity-60'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-gray-900">{promo.event_name}</h3>
            {isActive && (
              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                LIVE
              </span>
            )}
            {isUpcoming && daysUntil <= 14 && (
              <span className="bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                {daysUntil} days
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <Calendar size={14} />
              <span>{promo.start_date} to {promo.end_date}</span>
            </div>
            <span>({duration} days)</span>
          </div>

          <div className="flex items-center gap-3">
            <span className={`text-xs px-2 py-1 rounded-full ${promoTypeColors[promo.promo_type] || 'bg-gray-100 text-gray-800'}`}>
              {promo.promo_type}
            </span>
            <span className="text-xs text-gray-500">
              Channels: {promo.channels}
            </span>
          </div>
        </div>

        <div className="text-right">
          <div className="flex items-center gap-1 text-green-600">
            <TrendingUp size={18} />
            <span className="text-xl font-bold">+{Math.round(promo.expected_uplift * 100)}%</span>
          </div>
          <p className="text-xs text-gray-500">Expected uplift</p>
        </div>
      </div>
    </div>
  );
}

function MonthCalendar({ promos }) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  // Get promos by month
  const promosByMonth = {};
  promos?.forEach(promo => {
    const startDate = new Date(promo.start_date);
    const monthKey = `${startDate.getFullYear()}-${startDate.getMonth()}`;
    if (!promosByMonth[monthKey]) {
      promosByMonth[monthKey] = [];
    }
    promosByMonth[monthKey].push(promo);
  });

  return (
    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
      {months.map((month, index) => {
        const year = index < currentMonth ? currentYear + 1 : currentYear;
        const monthKey = `${year}-${index}`;
        const monthPromos = promosByMonth[monthKey] || [];
        const isCurrentMonth = index === currentMonth && year === currentYear;

        return (
          <div
            key={monthKey}
            className={`p-3 rounded-lg border ${
              isCurrentMonth
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white'
            }`}
          >
            <p className={`text-sm font-medium ${isCurrentMonth ? 'text-blue-700' : 'text-gray-900'}`}>
              {month}
            </p>
            <p className="text-xs text-gray-400">{year}</p>
            {monthPromos.length > 0 ? (
              <div className="mt-2 space-y-1">
                {monthPromos.map((promo, i) => (
                  <div
                    key={i}
                    className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded truncate"
                    title={promo.event_name}
                  >
                    {promo.event_name}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-xs text-gray-300">No events</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function PromoCalendar() {
  const { data: promos, isLoading, error } = usePromos();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        Error loading promos: {error.message}
      </div>
    );
  }

  const today = new Date();
  const upcomingPromos = promos
    ?.filter(p => new Date(p.end_date) >= today)
    ?.sort((a, b) => new Date(a.start_date) - new Date(b.start_date)) || [];

  const pastPromos = promos
    ?.filter(p => new Date(p.end_date) < today)
    ?.sort((a, b) => new Date(b.start_date) - new Date(a.start_date)) || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Promo Calendar</h1>
        <p className="text-gray-500">Upcoming sales events and expected demand impact</p>
      </div>

      {/* Year Overview */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Year Overview</h2>
        <MonthCalendar promos={promos} />
      </div>

      {/* Upcoming Events */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming & Active Events</h2>
        {upcomingPromos.length > 0 ? (
          <div className="space-y-4">
            {upcomingPromos.map((promo, index) => (
              <PromoCard key={index} promo={promo} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-center py-8">
            <Calendar className="mx-auto text-gray-300" size={48} />
            <p className="mt-4 text-gray-500">No upcoming promotions</p>
          </div>
        )}
      </div>

      {/* Impact Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Preparation Checklist</h2>
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
            <Tag className="text-yellow-600 mt-0.5" size={18} />
            <div>
              <p className="font-medium text-gray-900">Stock up for peak events</p>
              <p className="text-sm text-gray-600">
                Ensure hero SKUs have 6+ weeks of stock before major promos like Diwali and Pink Friday
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
            <TrendingUp className="text-blue-600 mt-0.5" size={18} />
            <div>
              <p className="font-medium text-gray-900">Adjust forecasts for promotions</p>
              <p className="text-sm text-gray-600">
                Apply expected uplift multipliers when calculating reorder quantities
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
            <Calendar className="text-green-600 mt-0.5" size={18} />
            <div>
              <p className="font-medium text-gray-900">Consider lead times</p>
              <p className="text-sm text-gray-600">
                With 45-60 day lead times, place orders at least 8 weeks before major events
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Past Events */}
      {pastPromos.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Past Events</h2>
          <div className="space-y-4">
            {pastPromos.slice(0, 3).map((promo, index) => (
              <PromoCard key={index} promo={promo} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
